import React, { useEffect, useState } from "react";
import { Image as ImageIcon, X, Upload, Camera, Layers } from "lucide-react";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const galleryImageUrl = (id, size = 600) => `${BACKEND}/api/public/photos/${id}/image.jpg?size=${size}&crop=0`;
const siteImageUrl = (key) => `${BACKEND}/api/public/site-images/${key}.jpg`;

/**
 * Unified image picker modal.
 *
 * Displays three tabs:
 *   1. Site images — brand/hero slots uploaded in Admin → Site images
 *   2. Class photos — the /gallery pool (from `photos` collection)
 *   3. Upload new — file input
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onPick: (val) => void
 *       For gallery/site: val is `{src}` where src is a URL or data URL
 *       For upload: val is `{src, image_base64, mime_type, filename}`
 *   - title: header copy
 */
export default function ImagePicker({ open, onClose, onPick, title = "Pick an image" }) {
  const [tab, setTab] = useState("site");
  const [photos, setPhotos] = useState([]);
  const [siteImages, setSiteImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      // Compact listing — skips image_base64 so a 100-photo library doesn't
      // push 50 MB through the picker. Thumbnails are loaded one-by-one via
      // /api/public/photos/<id>/image.jpg below.
      api.get("/admin/photos?compact=1").catch(() => ({ data: [] })),
      api.get("/admin/site-images").catch(() => ({ data: { keys: [], overrides: {} } })),
    ]).then(([p, s]) => {
      setPhotos(p.data || []);
      const entries = Object.entries(s.data?.overrides || {}).map(([key, v]) => ({
        key,
        mime: v.mime_type || "image/jpeg",
        base64: v.image_base64,
      })).filter((e) => e.base64);
      setSiteImages(entries);
      setLoading(false);
    });
  }, [open]);

  if (!open) return null;

  const onUpload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      alert("Please upload a JPEG / PNG / WebP image");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToB64(file);
      const [header, body] = dataUrl.split(",");
      const mime = header.slice(5, header.indexOf(";"));
      onPick({
        src: dataUrl,
        image_base64: body,
        mime_type: mime,
        filename: file.name,
      });
    } finally { setUploading(false); }
  };

  const TABS = [
    { key: "site", label: "Site images", icon: Layers, count: siteImages.length },
    { key: "gallery", label: "Class photos", icon: Camera, count: photos.length },
    { key: "upload", label: "Upload new", icon: Upload },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="admin-surface w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-picker-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E0D8]">
          <div>
            <h3 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><ImageIcon size={18} /> {title}</h3>
            <p className="text-xs text-[#6E6074] mt-1">Tap any image to use it. New uploads become part of your library automatically.</p>
          </div>
          <button onClick={onClose} className="text-[#A685BD] p-1" data-testid="image-picker-close"><X size={18} /></button>
        </div>

        <div className="flex gap-1 px-5 pt-3 border-b border-[#E5E0D8]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`image-picker-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                  active
                    ? "border-[#5D2C8F] text-[#5D2C8F] bg-[#FAF6EE]"
                    : "border-transparent text-[#6E6074] hover:text-[#5D2C8F]"
                }`}
              >
                <Icon size={14} /> {t.label}
                {t.count != null && <span className="ml-1 text-[11px] text-[#A685BD]">({t.count})</span>}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-center text-[#6E6074] py-10">Loading your library…</div>}

          {!loading && tab === "site" && (
            siteImages.length === 0 ? (
              <div className="text-center text-[#6E6074] py-10">
                No site images uploaded yet. Head to <strong>Admin → Site images</strong> to add your hero, tutor portrait, etc.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="image-picker-site-grid">
                {siteImages.map((si) => {
                  // Two srcs: a public URL ref (what we hand back to the
                  // caller — small, server-served, won't bloat downstream
                  // Mongo docs) and a data URL fallback for the inline
                  // thumbnail preview (avoids a flash of unstyled content
                  // because we already have the bytes in hand).
                  const ref = siteImageUrl(si.key);
                  const dataPreview = `data:${si.mime};base64,${si.base64}`;
                  return (
                    <button
                      key={si.key}
                      onClick={() => onPick({ src: ref, site_key: si.key })}
                      className="group relative aspect-square overflow-hidden rounded-lg border-2 border-transparent hover:border-[#5D2C8F] transition-all bg-[#FAF6EE]"
                      data-testid={`image-picker-site-${si.key}`}
                    >
                      <img src={dataPreview} alt={si.key} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                        <span className="text-[10px] text-white font-medium capitalize">{si.key.replace(/([A-Z])/g, " $1").trim()}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {!loading && tab === "gallery" && (
            photos.length === 0 ? (
              <div className="text-center text-[#6E6074] py-10">
                No class photos uploaded yet. Go to <strong>Admin → Photos &amp; Social</strong> to upload some.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="image-picker-gallery-grid">
                {photos.map((ph) => (
                  <button
                    key={ph.id}
                    onClick={() => onPick({ src: galleryImageUrl(ph.id, 1200), photo_id: ph.id })}
                    className="group relative aspect-square overflow-hidden rounded-lg border-2 border-transparent hover:border-[#5D2C8F] transition-all"
                    data-testid={`image-picker-gallery-${ph.id}`}
                  >
                    <img src={galleryImageUrl(ph.id, 400)} alt={ph.alt_text || "Class photo"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            )
          )}

          {!loading && tab === "upload" && (
            <div className="text-center py-10">
              <Upload size={40} className="mx-auto text-[#A685BD] mb-3" />
              <p className="text-sm text-[#6E6074] mb-4">Pick an image from your computer. Supported: JPEG, PNG, WebP.</p>
              <label className="sac-btn-primary cursor-pointer inline-flex">
                <Upload size={14} /> {uploading ? "Uploading…" : "Choose file"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="image-picker-upload-input" />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}
